import React, { useEffect, useState } from "react";
import { Link } from "gatsby";
import { getImage, GatsbyImage } from "gatsby-plugin-image";
import { getBlogPath } from "../../utils/blogRoutes";

const DESKTOP_CAROUSEL_QUERY = "(min-width: 1024px)";

const truncateExcerpt = (excerpt) => {
	if (!excerpt || excerpt.length <= 220) {
		return excerpt;
	}

	const cutoffIndex = excerpt.indexOf(" ", 220);
	return cutoffIndex === -1
		? `${excerpt} ...`
		: `${excerpt.slice(0, cutoffIndex)} ...`;
};

const getVisiblePageIndexes = (pageCount, pageIndex, maxVisibleDots) => {
	if (pageCount <= maxVisibleDots) {
		return Array.from({ length: pageCount }, (_, index) => index);
	}

	const halfWindow = Math.floor(maxVisibleDots / 2);
	let startIndex = Math.max(0, pageIndex - halfWindow);
	let endIndex = startIndex + maxVisibleDots;

	if (endIndex > pageCount) {
		endIndex = pageCount;
		startIndex = Math.max(0, endIndex - maxVisibleDots);
	}

	return Array.from(
		{ length: endIndex - startIndex },
		(_, index) => startIndex + index
	);
};

function AdviceArticleCard({ title, slug, excerpt, publishedAt, image }) {
	return (
		<Link to={getBlogPath(slug)} className="advice-article-card">
			<div className="advice-article-card__image">
				{image ? (
					<GatsbyImage placeholder="blurred" image={getImage(image)} alt="" />
				) : (
					<span aria-hidden="true">Edyn advice</span>
				)}
			</div>
			<div className="advice-article-card__content">
				<p className="advice-article-card__date">{publishedAt}</p>
				<h3>{title}</h3>
				{excerpt && <p>{truncateExcerpt(excerpt)}</p>}
				<span className="advice-article-card__link">
					Read guide <span aria-hidden="true">&#8594;</span>
				</span>
			</div>
		</Link>
	);
}

export default function AdviceArticleCarousel({ posts }) {
	const [visiblePosts, setVisiblePosts] = useState(3);
	const [pageIndex, setPageIndex] = useState(0);
	const isSingleColumnCarousel = visiblePosts === 1;
	const pageCount = Math.max(1, Math.ceil(posts.length / visiblePosts));
	const currentPosts = posts.slice(
		pageIndex * visiblePosts,
		(pageIndex + 1) * visiblePosts
	);
	const visiblePageIndexes = getVisiblePageIndexes(
		pageCount,
		pageIndex,
		isSingleColumnCarousel ? 5 : pageCount
	);

	useEffect(() => {
		if (typeof window === "undefined") {
			return undefined;
		}

		const mediaQuery = window.matchMedia(DESKTOP_CAROUSEL_QUERY);
		const updateVisiblePosts = () => {
			setVisiblePosts(mediaQuery.matches ? 3 : 1);
		};

		updateVisiblePosts();

		if (mediaQuery.addEventListener) {
			mediaQuery.addEventListener("change", updateVisiblePosts);
			return () => mediaQuery.removeEventListener("change", updateVisiblePosts);
		}

		mediaQuery.addListener(updateVisiblePosts);
		return () => mediaQuery.removeListener(updateVisiblePosts);
	}, []);

	useEffect(() => {
		setPageIndex(0);
	}, [visiblePosts, posts.length]);

	const updatePage = (nextPageIndex) => {
		if (nextPageIndex < 0) {
			setPageIndex(pageCount - 1);
			return;
		}

		if (nextPageIndex >= pageCount) {
			setPageIndex(0);
			return;
		}

		setPageIndex(nextPageIndex);
	};

	return (
		<div className="advice-carousel">
			<div className="advice-carousel__cards">
				{currentPosts.map(
					({ title, slug, excerpt, published_at, feature_image_sharp }) => (
						<AdviceArticleCard
							key={slug}
							title={title}
							slug={slug}
							excerpt={excerpt}
							publishedAt={published_at}
							image={feature_image_sharp}
						/>
					)
				)}
			</div>
			{pageCount > 1 && (
				<div className="advice-carousel__controls">
					<button
						type="button"
						className="advice-carousel__button"
						onClick={() => updatePage(pageIndex - 1)}
					>
						<span aria-hidden="true">&#8592;</span> Previous
					</button>
					<div className="advice-carousel__dots" aria-label="Carousel pagination">
						{visiblePageIndexes.map((index) => (
							<button
								type="button"
								key={`page-${index + 1}`}
								className={`advice-carousel__dot${
									index === pageIndex ? " is-active" : ""
								}`}
								onClick={() => updatePage(index)}
								aria-label={`Show page ${index + 1}`}
								aria-current={index === pageIndex ? "page" : undefined}
							/>
						))}
					</div>
					<button
						type="button"
						className="advice-carousel__button"
						onClick={() => updatePage(pageIndex + 1)}
					>
						Next <span aria-hidden="true">&#8594;</span>
					</button>
				</div>
			)}
		</div>
	);
}
